import { useTinyHost } from "@tiny/host-contract";
import React from "react";
import { Paginator, Box, EmptyState } from "@tiny/components";
import AudienceFilter from "./AudienceFilter";
import { AudienceListProvider } from "./AudienceList.context";
import { useAudienceList } from "./AudienceList.hooks";
import { AudienceListProps } from "./AudienceList.types";
import { useStyles } from "./AudienceList.styles";
import AudienceRow from "./AudienceRow";
import Search from "@tiny/icons/generated/Search";
import ComponentLoader from "@tiny/components/src/ComponentLoader/ComponentLoader";
import AudienceMergeDialog from "./AudienceMergeDialog";
import AudienceSummaryDialog from "../AudienceSummaryDialog";

import SegmentPerformanceDialog from "../SegmentPerformanceDialog";

function AudienceList(props: AudienceListProps) {
  const { slots } = useTinyHost();
  const audienceListView = useAudienceList(props);

  const classes = useStyles();

  const {
    audiences,
    boxProps,
    closeMergeDialog,
    itemsPerPage,
    itemsPerPageOptions,
    isEmpty,
    isSegmentLoading,
    onPageChange,
    selected: selectedAudience,
    onItemsPerPageChange,
    page,
    showMergeDialog,
    visible,
    isAudienceSummaryOpen,
    handleCloseAudienceSummary,
    selectedSegmentIds,
    closeTrafficControl,
    showTrafficControlDialog,
    deselectAll,
    isSegmentPerformanceOpen,
    handleCloseSegmentPerformance,
  } = audienceListView;

  if (!visible) {
    return null;
  }

  return (
    <>
      <AudienceListProvider value={audienceListView}>
        <Box {...boxProps}>
          <AudienceFilter />
          {isSegmentLoading ? (
            <ComponentLoader height="400px" />
          ) : isEmpty ? (
            <Box bgcolor="grey.50" pt={10} pb={10}>
              <EmptyState
                title="No audiences found"
                message="Try some other search keywords. Tip: Try remembering the audience name"
                icon={<Search />}
                iconColor="primary"
                circleColor="white"
                align="center"
              />
            </Box>
          ) : (
            <ul className={classes.list}>
              {audiences?.content?.map((audience) => (
                <AudienceRow key={audience.id} audience={audience} />
              ))}
            </ul>
          )}
          <Paginator
            mt={4}
            itemsPerPageOptions={itemsPerPageOptions}
            page={page}
            itemsPerPage={itemsPerPage}
            totalCount={audiences?.totalElements}
            onPageChange={onPageChange}
            onItemsPerPageChange={onItemsPerPageChange}
            itemsPerPageLabel="Results"
          />
        </Box>
        <AudienceMergeDialog
          audiences={selectedAudience}
          onClose={closeMergeDialog}
          open={showMergeDialog}
        />

        {showTrafficControlDialog &&
          slots?.trafficShapingDialog?.({
            segments:
              selectedAudience?.map((segment, index) => ({
                id: segment.id || "",
                name: segment.name,
                priority: index,
              })) ?? [],
            open: showTrafficControlDialog,
            onClose: closeTrafficControl,
            onSave: () => {
              deselectAll();
            },
          })}

        <AudienceSummaryDialog
          open={isAudienceSummaryOpen}
          onClose={handleCloseAudienceSummary}
          segmentIds={selectedSegmentIds}
        />
        <SegmentPerformanceDialog
          open={isSegmentPerformanceOpen}
          onClose={handleCloseSegmentPerformance}
          segmentId={selectedSegmentIds[0] ?? ""}
        />
      </AudienceListProvider>
    </>
  );
}

export default AudienceList;
